import React, { useEffect } from 'react'
import PropTypes from 'prop-types'

import { useFormContext } from 'react-hook-form'
import { Box, Button, Collapse, IconButton, List, ListItem, ListItemText, Typography } from '@mui/material'
import Grid from '@mui/material/Grid'

import ChevronRight from '@mui/icons-material/ChevronRight'
import ExpandMore from '@mui/icons-material/ExpandMore'

import { get } from 'utils/lodash'
import { CONTACT_METHOD_TYPES, CONTACT_METHODS, METHOD_TYPES } from 'settings/constants/contacts'
import { HHFormSelectField, HHFormTextField } from 'components/form-fields/v5'

import T from 'T'
import SelectItem from 'components/form-fields/v5/SelectItem'
import HHPhoneField from 'components/form-fields/v5/HHPhoneField'
import ContactMethodIcon from 'components/customer-details/content/contacts/ContactMethodIcon'
import ContactMethodValueFields from 'components/customer-details/content/contacts/ContactMethodValueFields'

const { EMAIL, FAX } = CONTACT_METHODS
const { PERSONAL, WORK } = CONTACT_METHOD_TYPES

const EMAIL_PLACEHOLDER = 'place.holder@email.com'
const MOBILE_PLACEHOLDER = '(858) 888-1324'

const BeneficialOwnerContactInfo = ({ isExpanded = false, index, contactMethod = {}, onExpandChange, onDelete }) => {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext()

  const methodType = get(contactMethod, 'method')
  const contactMethodType = get(contactMethod, 'type')
  const methodValue = get(contactMethod, 'data.value', '')
  const methodPhoneExtension = get(contactMethod, 'data.phoneExtension', '')
  const isValidContactMethod = get(errors, `contactInfos[${index}].data.value`)

  const handleClick = () => {
    onExpandChange(!isExpanded)
  }

  const renderMethodText = () => {
    const isEmailMethod = methodType === EMAIL
    if (!methodValue) {
      return (
        <Typography color="textSecondary" ml={0.5}>
          {isEmailMethod ? EMAIL_PLACEHOLDER : MOBILE_PLACEHOLDER}
        </Typography>
      )
    }

    return (
      <Typography noWrap ml={0.5}>
        {isEmailMethod ? methodValue : <HHPhoneField displayType="text" value={methodValue} phoneExtension={methodPhoneExtension} />}
      </Typography>
    )
  }

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (!name) return
      const [field, subIndex, subfield] = name.split('.')
      // Fire when the *method* dropdown (Email/Phone/Fax/Mobile) changes — not the
      // *type* (Work/Personal) dropdown. Switching method clears the value-shaped
      // fields and aligns the Work/Personal default to the new method.
      if (subfield === 'method') {
        const selectedMethod = get(value, `${field}.${subIndex}.${subfield}`)
        setValue(`contactInfos[${subIndex}].type`, selectedMethod === FAX ? WORK : PERSONAL)
        setValue(`contactInfos[${subIndex}].data.value`, '')
        setValue(`contactInfos[${subIndex}].data.phoneExtension`, '')
      }
    })

    return () => subscription.unsubscribe()
  }, [watch])

  return (
    <>
      <ListItem>
        <IconButton size="small" onClick={handleClick}>
          {isExpanded ? <ExpandMore /> : <ChevronRight />}
        </IconButton>

        <ListItemText>
          <Typography variant="h6" fontWeight={400}>
            {methodType}
          </Typography>
        </ListItemText>

        <Button color="error" variant="outlined" onClick={onDelete} sx={{ ml: 1 }} size="small">
          {T.DELETE}
        </Button>
      </ListItem>
      <Box sx={{ pl: 6, pr: 2, py: 0 }} display="flex" alignItems="center" minHeight={20}>
        <ContactMethodIcon type={methodType} />
        {renderMethodText()}
      </Box>
      <Collapse in={isExpanded} timeout="1000">
        <List component="div" disablePadding>
          <ListItem disablePadding sx={{ pl: 5, pr: 1, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Grid container sx={{ width: '100%' }} mt={1.5} rowGap={1} spacing={1}>
              <Grid size={4}>
                <HHFormTextField control={control} fullWidth select name={`contactInfos.${index}.method`}>
                  {Object.values(CONTACT_METHODS).map(method => (
                    <SelectItem value={method} sx={{ '&.Mui-selected svg': { color: 'primary.main' } }}>
                      <ListItem disablePadding>
                        <ContactMethodIcon type={method} />
                        <Typography ml={1}>{method}</Typography>
                      </ListItem>
                    </SelectItem>
                  ))}
                </HHFormTextField>
              </Grid>
              <Grid size="grow">
                <HHFormSelectField
                  value={contactMethodType}
                  control={control}
                  label="Contact type"
                  options={METHOD_TYPES[methodType]}
                  getOptionValue={option => option}
                  getOptionLabel={option => option}
                  fullWidth
                  deprecatedLabel={false}
                  name={`contactInfos.${index}.type`}
                />
              </Grid>
              <ContactMethodValueFields
                control={control}
                methodType={methodType}
                methodValue={methodValue}
                methodPhoneExtension={methodPhoneExtension}
                valueFieldName={`contactInfos.${index}.data.value`}
                extensionFieldName={`contactInfos.${index}.data.phoneExtension`}
                error={isValidContactMethod}
              />
            </Grid>
          </ListItem>
        </List>
      </Collapse>
    </>
  )
}

BeneficialOwnerContactInfo.propTypes = {
  isExpanded: PropTypes.bool,
  index: PropTypes.number,
  contactMethod: PropTypes.object,
  onExpandChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
}

export default BeneficialOwnerContactInfo
